import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  Clock,
  Shield,
  Globe,
  CheckCircle2,
  Sparkles,
  MapPin,
  Headphones,
  ArrowRight,
} from 'lucide-react';
import Footer from '../shared/Footer';
import cookieManager from '../../utils/cookieManager';

const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

const highlights = [
  {
    title: '航司分销直采',
    desc: '对接航旅分销渠道，覆盖国内主要城市与全球 490+ 机场，支持国内航线及中美、中欧等国际主线报价实时查询。',
    icon: Plane,
  },
  {
    title: '价格人工复核',
    desc: '系统比价后由客服人工复核再确认出票，避免机器误订；报价透明，税费明细一目了然。',
    icon: CheckCircle2,
  },
  {
    title: '免费改退咨询',
    desc: '下单前后均可咨询退改签政策，客服协助处理航司退改，行程有变化不慌张。',
    icon: Headphones,
  },
  {
    title: '订单全程可查',
    desc: '提交订单后可随时查看状态：待确认 → 已确认 → 已付款 → 已出票，进度透明可控。',
    icon: Clock,
  },
  {
    title: '中文全程服务',
    desc: '中文界面、中文客服，乘机人信息对照护照拼音填写，无需英文沟通。',
    icon: Globe,
  },
  {
    title: '数据安全',
    desc: '护照等敏感信息仅用于出票，仅本人与管理员可查看订单，任务数据不留存多余副本。',
    icon: Shield,
  },
];

const steps = [
  '登录账号后，输入出发地、目的地和日期，实时查询航班与报价',
  '对比航班时刻、时长、中转与价格，选择合适的航班',
  '按护照拼音填写乘机人信息（成人 / 儿童 / 婴儿）',
  '提交订单，订单进入「待确认」状态（此时不收款）',
  '客服人工复核价格与舱位后确认订单，并与您约定支付方式',
  '付款出票后订单状态更新为「已出票」，票号可在订单详情中查看',
];

const faqList = [
  {
    q: '为什么提交订单后是「待确认」状态？',
    a: '机票价格实时波动，为保证您以准确价格出票，提交订单后由客服人工向供应商复核价格与舱位，确认后再与您约定支付方式。复核通过前不会收取任何费用。',
  },
  {
    q: '怎么付款？',
    a: '客服确认订单后，支持微信支付等方式付款；付款后 1-2 小时内出票（航司放票时间可能有延迟），票号会显示在订单详情中。',
  },
  {
    q: '儿童票、婴儿票怎么算价格？',
    a: '儿童（2-12 岁）约为成人票价的 75%，婴儿（2 岁以下不占座）约为 10%，最终以客服确认的价格为准。',
  },
  {
    q: '乘机人信息怎么填？',
    a: '姓名请按护照拼音填写（姓与名分开），护照号与有效期务必与护照一致；填写错误可能导致无法出票或登机。',
  },
  {
    q: '可以退改签吗？',
    a: '不同航班退改政策不同（结果页会标注是否可退）。下单前后均可咨询客服了解具体航班的退改规则。',
  },
  {
    q: '报价的有效期是多久？',
    a: '机票运价实时波动，查询结果一般 30 分钟内有效；超时后价格可能变化，请以客服确认的最终价格为准。',
  },
  {
    q: '我的护照信息安全吗？',
    a: '护照信息仅用于向航司/供应商出票，订单仅本人与管理员可见；系统不会将您的信息用于其他用途。',
  },
  {
    q: '需要注册账号吗？',
    a: '需要。查询航班与提交订单都需登录账号（与美签/越南签证/泰国入境卡站共用同一账号，一处注册全站通用）。注册免费，登录后即可查询实时报价。',
  },
];

const SearchCallout = () => {
  const isLoggedIn = cookieManager.isLoggedIn();
  return (
    <div className="rounded-3xl bg-white/95 border border-sky-100 shadow-xl p-8">
      <div className="flex items-center gap-2 text-sky-700 font-bold mb-3">
        <Plane className="w-5 h-5" /> 查询机票
      </div>
      <p className="text-gray-600 text-sm leading-relaxed mb-6">
        支持单程、往返与多程行程，可按直飞、起降时段、航空公司筛选，
        并直接看到每档报价的退改条件与中转详情。
      </p>
      <ul className="text-sm text-gray-500 space-y-2 mb-7">
        {['490+ 全球机场，中文搜索', '实时航司报价，明细透明', '中转与转场提示，避免踩坑'].map((t) => (
          <li key={t} className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 flex-shrink-0" />
            {t}
          </li>
        ))}
      </ul>
      <Link
        to="/flight"
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold text-lg hover:from-sky-600 hover:to-cyan-600 hover:shadow-lg transition-all duration-300"
      >
        {isLoggedIn ? '开始查询航班' : '登录后查询航班'}
        <ArrowRight className="w-5 h-5" />
      </Link>
      <p className="text-xs text-gray-400 text-center mt-3">
        {isLoggedIn ? '实时比价 · 下单仅需 1 分钟' : '注册登录即可查询 · 下单仅需 1 分钟'}
      </p>
    </div>
  );
};

const FlightHome = () => {
  usePageTitle('国内 / 国际机票查询预订 | 航线比价 · 人工确认 · 一站式出票');
  const isLoggedIn = cookieManager.isLoggedIn();

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-cyan-50 to-white">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="旅报报 logo" className="w-9 h-9" />
            <div className="font-extrabold text-gray-900">旅报报·机票</div>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="https://lvbaobao.space/"
              className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:border-sky-500 hover:text-sky-600 hover:bg-white transition-all"
            >
              <span className="sm:hidden">返回主站</span>
              <span className="hidden sm:inline">返回 lvbaobao.space</span>
            </a>
            <nav className="hidden md:flex items-center gap-5 text-sm text-gray-700">
              <Link to="/flight" className="hover:text-gray-900">查询机票</Link>
              <a href="#flow" className="hover:text-gray-900">订购流程</a>
              <a href="#faq" className="hover:text-gray-900">FAQ</a>
              {isLoggedIn ? (
                <Link to="/flight/orders" className="px-4 py-2 rounded-lg bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-medium hover:from-sky-600 hover:to-cyan-600 transition-all">
                  我的订单
                </Link>
              ) : (
                <Link to="/auth/login" className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-sky-500 hover:text-sky-600 transition-all">
                  登录
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-70">
            <div className="absolute -top-20 -left-10 w-80 h-80 bg-sky-200 blur-[120px]" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-200 blur-[120px]" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 text-white text-xs font-medium mb-4">
                <Plane className="w-4 h-4 mr-2" /> 国内 / 国际航线比价
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
                国内 / 国际机票查询与预订，人工确认更安心
              </h1>
              <p className="mt-5 text-gray-700 text-lg md:text-xl">
                实时查询国内与国际航线航班和报价，支持单程 / 往返 / 多程，<strong>下单后客服人工复核</strong>再确认收款，价格透明、进度可查，出行省心。
              </p>
              <div className="mt-8 flex flex-wrap gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-sky-600" />
                  <span>覆盖 490+ 全球机场</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-sky-600" />
                  <span>实时比价 · 明细透明</span>
                </div>
                <div className="flex items-center gap-2">
                  <Headphones className="w-5 h-5 text-sky-600" />
                  <span>客服 1v1 跟单</span>
                </div>
              </div>
            </div>

            {/* 查询入口。首页只负责讲清楚服务、把人引到搜索页——
                行程类型、人数、舱位这些在搜索页一次填完，不在这里做半套表单 */}
            <SearchCallout />
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 text-center">核心能力</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              从查询到出票的一站式机票预订服务
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {highlights.map((item, idx) => (
                <div key={idx} className="p-6 rounded-3xl border border-sky-100 hover:shadow-xl hover:border-sky-300 transition-all duration-300 bg-gradient-to-br from-white to-sky-50">
                  <item.icon className="w-12 h-12 text-sky-600 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Flow Section */}
        <section id="flow" className="py-16 bg-gradient-to-b from-sky-50 to-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 text-center">订购流程</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              简单 6 步，从查询到出票
            </p>
            <div className="space-y-4">
              {steps.map((step, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 text-white flex items-center justify-center font-bold text-lg">
                    {idx + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-900 font-medium">{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 text-center">常见问题</h2>
            <p className="text-center text-gray-600 mb-12">
              关于机票查询与预订的常见问题解答
            </p>
            <div className="space-y-4">
              {faqList.map((item, idx) => (
                <details key={idx} className="bg-gradient-to-r from-sky-50 to-cyan-50 border border-sky-100 rounded-2xl p-6 hover:shadow-md transition-all duration-300 cursor-pointer group">
                  <summary className="font-semibold text-gray-900 flex items-center justify-between">
                    <span>{item.q}</span>
                    <span className="text-sky-600 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="mt-4 text-gray-600 text-sm leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-sky-500 to-cyan-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              准备好了吗？
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              立即查询航班，1 分钟提交订单，客服人工确认后出票
            </p>
            <Link
              to="/flight"
              className="inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-white text-sky-600 font-bold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              查询机票
            </Link>
          </div>
        </section>
      </main>

      <Footer className="bg-white" />
    </div>
  );
};

export default FlightHome;
